import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DialogBox, DialogBoxFooterType, NoLink, FileSelector } from "~/core";
import "./component.css";
import { AppHelpers } from "@ai360/core";
import { messages } from "../i18n-messages";
import { createAddLinkLabelText, messages as globalMessages } from "~/i18n-messages";
import classNames from "classnames";

// constants
export const PROPS_BASE64STRING = "base64String";
export const DEFAULT_IMAGE_TYPE = "*";
export const SVG = "svg";
export const SVG_XML = "image/svg+xml";
export const ICO = "ico";

export const PhotoSimple = ({
    className,
    formatMessage,
    onChange,
    title,
    isDeleteModalRequired = true,
    fileType,
    record,
    id,
}) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [previewImageData, setPreviewImageData] = useState(null);
    const [showValidIcoModal, setShowValidIcoModal] = useState(false);

    useEffect(() => {
        if (record) {
            setPreviewImageData(
                !AppHelpers.fileIsSvgMarkup(record)
                    ? AppHelpers.base64StringToBase64Image(record, fileType)
                    : record
            );
        } else {
            setPreviewImageData(null);
        }
    }, [record, fileType]);

    const onDelete = () => {
        onChange("");
        setPreviewImageData(null);
        setIsDeleting(false);
    };

    const setViewport = (imageString) => {
        setPreviewImageData(imageString);
        onChange(imageString);
    };

    const onAdd = ({ files }) => {
        const file = files[0];
        if (file.type === SVG_XML) {
            if (fileType === ICO) {
                setShowValidIcoModal(true);
            } else {
                AppHelpers.fileToXML(file).then((xml) => {
                    if (xml) {
                        setViewport(xml);
                    }
                });
            }
        } else {
            AppHelpers.fileToBase64(file).then((base64) => {
                if (base64) {
                    const image = fileType ? AppHelpers.base64ImageToBase64string(base64) : base64;
                    onChange(image);
                    setPreviewImageData(base64);
                }
            });
        }
    };

    const imageType = fileType ? fileType : DEFAULT_IMAGE_TYPE;

    return (
        <div className={classNames("photo-simple", className)}>
            {!previewImageData ? null : (
                <div>
                    <div className="delete-link-container">
                        <NoLink
                            className="delete-link"
                            label={formatMessage(messages.delete)}
                            onClick={() => setIsDeleting(true)}
                        />
                    </div>
                    {AppHelpers.fileIsSvgMarkup(previewImageData) ? (
                        <div
                            className={"image-thumbnail"}
                            dangerouslySetInnerHTML={{
                                __html: previewImageData,
                            }}
                        ></div>
                    ) : (
                        <img alt="" src={previewImageData} className="image-thumbnail" />
                    )}
                </div>
            )}
            {!isDeleteModalRequired ? null : (
                <DialogBox
                    footerType={DialogBoxFooterType.YES_NO}
                    isOpen={isDeleting}
                    onAction={onDelete}
                    onClose={() => setIsDeleting(false)}
                    title={formatMessage(globalMessages.confirmTitle)}
                    testId="photo-simple-confirm-delete-modal"
                >
                    {formatMessage(globalMessages.deleteConfirmationMessage)}
                </DialogBox>
            )}
            {!showValidIcoModal ? null : (
                <DialogBox
                    isOpen={showValidIcoModal}
                    onClose={() => setShowValidIcoModal(false)}
                    title={formatMessage(messages.invalidFileType)}
                    testId="photo-simple-invalid-file-type-modal"
                >
                    {formatMessage(messages.selectValidIcoFile)}
                </DialogBox>
            )}
            {previewImageData ? null : (
                <FileSelector
                    fileType={`image/${imageType}`}
                    allowMultiple={true}
                    label={createAddLinkLabelText(formatMessage, messages.photo, { count: 1 })}
                    fileCompletedCallback={onAdd}
                    id={id}
                    className="add-link"
                    title={title}
                />
            )}
        </div>
    );
};

PhotoSimple.propTypes = {
    className: PropTypes.string,
    formatMessage: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    title: PropTypes.string,
    record: PropTypes.string,
    isDeleteModalRequired: PropTypes.bool,
    fileType: PropTypes.string,
    id: PropTypes.string.isRequired,
};
